import React, { useEffect, useState } from 'react';
import styles from './WorkplaceWorkerWorkplacesList.module.scss';
import FormWrapper from '../../../../../Components/Form/FormWrapper';
import CheckboxField from '../../../../../Components/Form/components/CheckboxField';
import FormMultiplier from '../../../../../Components/Form/FormMultiplier';
import InputField from '../../../../../Components/Form/components/InputField';
import WorkplaceSelectField from '../../../../Base/Components/Inputs/WorkplaceSelect/WorkplaceSelectField';
import RoleMultiSelectWithCheckListField from '../../../../Base/Components/Inputs/RoleMultiSelectWithCheckList/RoleMultiSelectWithCheckListField';
import FormMultiplierRemoveButton from '../../../../../Components/Form/FormMultiplierRemoveButton';
import LinkButton from '../../../../../Components/Button/LinkButton';
import { Theme } from '../../../../../Enums/Theme.enum';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
	useLazyWorkplaceRolesQuery,
	useUpdateWorkplaceMemberMutation,
	WorkplaceMember,
} from '../../../../../graphql/graphql';
import useFilters from '../../../../Base/Hooks/useFilters';
import useTranslation from '../../../../../Hooks/useTranslation';
import { useAppContext } from '../../../../../Services/AppContext';
import useRootWorkplace from '../../../Hooks/useRootWorkplace';
import Loading from '../../../../../Components/Loading';
import apiClient from '../../../../../Services/ApiClient';
import useApiClient from '../../../../Base/Hooks/useApiClient';
import { WorkplaceAclRole } from '../../../../Workplace/Enums/WorkplaceAclRole.enum';
import RemoveButton from '../../../../../Components/Button/RemoveButton';
import useRestApiClient from '../../../../Base/Hooks/useRestApiClient';

interface WorkplaceWorkerWorkplacesEditFormValues {
	superAdmin?: boolean;
	workplaces: {
		id: string;
		memberSearchableOutside: boolean;
		workplace: {
			label: string;
			value: string;
		};
		role: {
			label: string;
			value: string;
		}[];
	}[];
}

interface Props {
	userId: string;
	onUpdate: () => void;
	showAllWorkplaces: boolean;
	data: WorkplaceMember[];
	onDeleteFromAllWorkplaces: () => void;
}

interface WorkplaceMemberToDelete {
	id: string;
	workplaceId: string;
}

const WorkplaceWorkerWorkplacesEdit: React.FC<Props> = ({
	userId,
	onUpdate,
	showAllWorkplaces,
	data,
	onDeleteFromAllWorkplaces,
}) => {
	const { t } = useTranslation();
	const [updateWorkplaceMember] = useUpdateWorkplaceMemberMutation();
	const [getWorkplaceRoles] = useLazyWorkplaceRolesQuery();
	const { workplaceId, userData } = useAppContext();
	const {
		rootWorkplaceDataIsLoading,
		rootWorkplaceName,
		isUserSuperAdmin,
		rootWorkplaceId,
	} = useRootWorkplace(workplaceId, userId);
	const [isLoading, setIsLoading] = useState(false);
	const { displayError, displayGQLError } = useApiClient();
	const { checkEmptyString } = useFilters();
	const { showUnexpectedError } = useRestApiClient();

	const deleteOldWorkplaceMembers = async (
		workplaceMembersToDelete: WorkplaceMemberToDelete[],
	) => {
		for (const workplaceMemberToDelete of workplaceMembersToDelete) {
			try {
				apiClient.delete(
					`/workplaces/${workplaceMemberToDelete.workplaceId}/members/${workplaceMemberToDelete.id}`,
				);
			} catch (e) {
				displayError(e);
			}
		}
	};

	const handleSuperAdminRole = async (superAdmin?: boolean) => {
		const roleIds: string[] = [];

		if (superAdmin) {
			const { workplaceAclRoles } = await getWorkplaceRoles().unwrap();
			const superAdminRoleId = workplaceAclRoles?.data?.find(
				item => item?.key === WorkplaceAclRole.ADMIN,
			)?.id;
			if (superAdminRoleId) {
				roleIds.push(superAdminRoleId);
			}
		}

		const result = await updateWorkplaceMember({
			input: {
				workplaceMembers: [
					{
						workplaceId: rootWorkplaceId,
						userId,
						roleIds,
					},
				],
			},
		});
		displayGQLError(result);
	};

	const handleSubmit = async (
		values: WorkplaceWorkerWorkplacesEditFormValues,
	) => {
		console.log('values', values);
		setIsLoading(true);

		const workplaceMembersToDelete: WorkplaceMemberToDelete[] = [];

		for (const workplaceMember of data) {
			if (
				!values?.workplaces.some(
					item =>
						item.id === workplaceMember.id &&
						item.workplace.value === workplaceMember.workplace?.id,
				) &&
				workplaceMember.workplace?.id
			) {
				workplaceMembersToDelete.push({
					id: workplaceMember.id,
					workplaceId: workplaceMember.workplace?.id,
				});
			}
		}

		await deleteOldWorkplaceMembers(workplaceMembersToDelete);
		if (showAllWorkplaces) {
			await handleSuperAdminRole(values?.superAdmin);
		}

		const result = await updateWorkplaceMember({
			input: {
				workplaceMembers: values?.workplaces
					?.filter(item => item.workplace.value)
					?.map(workplaceMember => {
						const isDeleted = workplaceMembersToDelete.some(
							(item: WorkplaceMemberToDelete) => item.id === workplaceMember.id,
						);
						return {
							id: !isDeleted ? checkEmptyString(workplaceMember.id) : undefined,
							workplaceId: workplaceMember.workplace.value,
							userId,
							roleIds: workplaceMember?.role
								?.filter((item: any) => item.value)
								?.map((role: any) => {
									return role.value;
								}),
							memberSearchableOutside: workplaceMember?.memberSearchableOutside,
						};
					}),
			},
		});
		displayGQLError(result);
		if ('data' in result) {
			onUpdate();
		}
		setIsLoading(false);
	};

	useEffect(() => {
		if (showAllWorkplaces) {
			formMethods.reset({
				...formMethods.getValues(),
				...{ superAdmin: isUserSuperAdmin },
			});
		}
	}, [isUserSuperAdmin]);

	const validationSchema = yup.object({
		superAdmin: yup.boolean(),
		workplaces: yup
			.array()
			.of(
				yup.object().shape({
					id: yup.string().notRequired(),
					workplace: yup.object().shape({
						label: yup.string(),
						value: yup
							.string()
							.required(
								t('validation.workplaceWorkerWorkplacesList.workplace'),
							),
					}),
					role: yup
						.mixed()
						.nullable()
						.required(t('validation.workplaceWorkerWorkplacesList.role')),
				}),
			)
			.required(),
	});

	const formMethods = useForm({
		resolver: yupResolver(validationSchema),
		defaultValues: {
			workplaces: data.map(item => {
				return {
					id: item.id,
					helperId: item.id,
					workplace: {
						value: item.workplace?.id,
						label: item.workplace?.name,
					},
					role: item?.workplaceMemberRoles?.map(role => {
						return {
							value: role?.role?.id,
							label: role?.role?.name,
						};
					}),
					memberSearchableOutside: item?.memberSearchableOutside,
				};
			}),
		},
	});

	const workplacesValues =
		useWatch({
			control: formMethods.control,
			name: 'workplaces',
			defaultValue: [],
		}) ?? [];

	const selectedWorkplaceIds = workplacesValues.flatMap(
		w => w.workplace?.value ?? [],
	);

	const handleRemoveFromAllWorkplaces = async () => {
		try {
			const response = await apiClient.delete(
				`/workplaces/${workplaceId}/members/${userId}/all`,
			);
			if (response.status === 200) {
				onDeleteFromAllWorkplaces();
			} else {
				showUnexpectedError();
			}
		} catch (e) {
			displayError(e);
		}
	};

	if (rootWorkplaceDataIsLoading || isLoading) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper}>
			<FormWrapper formMethods={formMethods}>
				<div className={styles.workplacesWrapper}>
					{showAllWorkplaces && (
						<>
							<div className={styles.rootWorkplace}>
								<div className={styles.title}>{rootWorkplaceName}</div>
								<div>
									<CheckboxField
										name='superAdmin'
										tx='component.workplaceWorkerWorkplacesEdit.allowWorkerAccessToSuperAdminModule'
									/>
								</div>
							</div>
							<RemoveButton
								title='component.workplaceWorkerWorkplacesEdit.removeFromAllWorkplaces'
								confirmationText='component.workplaceWorkerWorkplacesEdit.removeFromAllWorkplacesText'
								onClick={handleRemoveFromAllWorkplaces}
								buttonClassName={styles.removeAllButton}
							/>
							<hr />
						</>
					)}
					<div className={styles.title}>
						{t(
							'component.doctorDetailComponent.workplacesToWhichTheWorkerIsAdded',
						)}
						:
					</div>
					<FormMultiplier
						name='workplaces'
						addButtonTitle='component.workplaceAndWorkers.addToAnotherWorkplace'
						allowAddForm={showAllWorkplaces}>
						{({ index, update, field }) => (
							<div className={styles.workplaceInForm}>
								<div className={styles.selectsWrapper}>
									<div className='d-none'>
										<InputField name='id' />
									</div>
									<div className={styles.item}>
										<WorkplaceSelectField
											name='workplace'
											portal
											disabled={
												!showAllWorkplaces ||
												data.some(
													item => item.id === workplacesValues[index]?.helperId,
												)
											}
											label='base.shared.workplace'
											managed
											isRequired
											excludedIds={selectedWorkplaceIds}
										/>
									</div>
									<div className={styles.item}>
										<RoleMultiSelectWithCheckListField
											name='role'
											excludeRoleKeys={[WorkplaceAclRole.ADMIN]}
											label='component.workplaceAndWorkers.roleOfWorker'
											portal
											isRequired
										/>
									</div>
									{(showAllWorkplaces || userData.id !== userId) && (
										<div>
											<FormMultiplierRemoveButton>
												<LinkButton
													title='base.shared.remove'
													theme={Theme.RED}
													className={styles.multiplierButton}
												/>
											</FormMultiplierRemoveButton>
										</div>
									)}
								</div>
								{data[index]?.code && (
									<div className={styles.code}>
										{t(
											'component.workplaceWorkerWorkplacesEdit.codeForPatients',
										)}
										: <span>{data[index].code}</span>
									</div>
								)}
								<CheckboxField
									name='memberSearchableOutside'
									tx='component.generalForm.memberSearchableOutside'
								/>
							</div>
						)}
					</FormMultiplier>
				</div>
				<SubmitButton title='base.shared.save' onSubmit={handleSubmit} />
			</FormWrapper>
		</div>
	);
};
export default WorkplaceWorkerWorkplacesEdit;
